import React, { useCallback, useEffect, useState } from 'react'
import { Box, Button, Chip, CircularProgress, Divider, Typography, useMediaQuery } from '@mui/material'
import UserLayout from '../../layouts/UserLayout'
import StackedBarChartOutlinedIcon from '@mui/icons-material/StackedBarChartOutlined'
import { useNavigate } from 'react-router-dom'
import { getFromStorage } from '../../utils/storage'
import axios from 'axios'
import { DOMAIN, api } from '../../utils/api'
import { handleError } from '../../utils/error'
import { Bar, Pie } from 'react-chartjs-2'
import { useDispatch } from 'react-redux'
import { currentUserActions, loadingActions } from '../../redux/store'
import PickDate from '../../components/PickDate'
import PickDateWithoutShift from '../../components/PickDateWithoutShift'
import { mainBlue, mainGreen, mainRed } from '../../utils/color'

export default function StatisticsAdmin() {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const mediaMd = useMediaQuery('(min-width:768px)')
	const [loadingStatistics, setLoadingStatistics] = useState(true)
	const token = getFromStorage('current-user')?.accessToken
	const [stores, setStores] = useState([])
	const [statistics, setStatistics] = useState([])
	const [timePick, setTimePick] = useState({
		shift: '',
		date: new Date().getDate()?.toString(),
		month: (new Date().getMonth() + 1)?.toString(),
		year: new Date().getFullYear()?.toString(),
	})

	const [statisticsCompare, setStatisticsCompare] = useState([])
	const [firstTimePick, setFirstTimePick] = useState({
		date: '',
		month: (new Date().getMonth() + 1)?.toString(),
		year: new Date().getFullYear()?.toString(),
	})

	const [secondTimePick, setSecondTimePick] = useState({
		date: '',
		month: (new Date().getMonth() + 1)?.toString(),
		year: new Date().getFullYear()?.toString(),
	})

	const setTime = (time) => {
		setTimePick(time)
	}

	const setFirstTime = (time) => {
		setFirstTimePick(time)
	}

	const setSecondTime = (time) => {
		setSecondTimePick(time)
	}

	useEffect(() => {
		if (firstTimePick.date === '' && secondTimePick.date !== '') {
			setSecondTimePick((secondTimePick) => ({
				date: '',
				month: secondTimePick.month,
				year: secondTimePick.year,
			}))
			return
		}
		if (firstTimePick.date !== '' && secondTimePick.date === '') {
			setSecondTimePick((secondTimePick) => ({
				date: firstTimePick.date,
				month: secondTimePick.month,
				year: secondTimePick.year,
			}))
			return
		}
	}, [firstTimePick, secondTimePick])

	const getStatistics = useCallback(
		async function () {
			const { shift, date, month, year } = timePick
			try {
				const response = await axios({
					url: `${DOMAIN}/admin/statistics/stores?shift=${shift}&date=${date}&month=${month}&year=${year}`,
					headers: {
						Authorization: `Bearer ${token}`,
					},
					method: 'GET',
					withCredentials: true,
				})
				setStatistics(response.data)
				setLoadingStatistics(false)
			} catch (error) {
				handleError(error)
				dispatch(currentUserActions.isLogout())
				navigate('/login')
			}
		},
		[token, timePick, navigate, dispatch],
	)

	useEffect(() => {
		getStatistics()
	}, [getStatistics, timePick])

	const postCompareStatistics = useCallback(
		async function () {
			try {
				const response = await axios({
					url: api.COMPARE_STORES_2_TIMES,
					headers: {
						Authorization: `Bearer ${token}`,
					},
					method: 'POST',
					data: { firstTimePick, secondTimePick },
					withCredentials: true,
				})
				setStatisticsCompare(response.data)
				setLoadingStatistics(false)
			} catch (error) {
				handleError(error)
				dispatch(currentUserActions.isLogout())
				navigate('/login')
			}
		},
		[token, firstTimePick, secondTimePick, dispatch, navigate],
	)

	useEffect(() => {
		postCompareStatistics()
	}, [postCompareStatistics])

	const getStores = useCallback(
		async function () {
			dispatch(loadingActions.isLoading())
			try {
				const response = await axios({
					url: api.GET_STORES_ADMIN,
					headers: {
						Authorization: `Bearer ${token}`,
					},
					method: 'GET',
					withCredentials: true,
				})
				setStores(response.data)
			} catch (error) {
				console.log(error.response.data.message)
			}
		},
		[token, dispatch],
	)

	useEffect(() => {
		getStores()
	}, [getStores])

	return (
		<UserLayout>
			<Box
				overflow='auto'
				sx={{
					position: 'fixed',
					margin: '0 auto',
					top: mediaMd ? 64 : 56,
					right: 0,
					bottom: 0,
					left: mediaMd ? 310 : 0,
				}}>
				<Box
					sx={{
						display: 'flex',
						flexWrap: 'wrap',
						maxWidth: 992,
						margin: '10px auto',
					}}>
					{stores?.map((store, index) => {
						return (
							<Box key={index} sx={{ width: '50%' }}>
								<Button
									key={index}
									onClick={() => {
										navigate(`/admin/statistics/store/${store._id}`)
									}}
									variant='contained'
									sx={{
										width: '90%',
										m: 1,
										padding: ' 10px',
										display: 'flex',
										justifyContent: 'flex-start',
										background: mainGreen,
										'&:hover': { background: mainGreen },
									}}>
									<Box
										sx={{
											display: 'flex',
											justifyContent: 'flex-start',
											alignItems: 'flex-end',
										}}>
										<StackedBarChartOutlinedIcon sx={{ fontSize: '20px' }} />
										<Typography sx={{ ml: 1, fontSize: '14px' }}>{store.name}</Typography>
									</Box>
								</Button>
							</Box>
						)
					})}
				</Box>
				{loadingStatistics ? (
					<Box
						display='flex'
						justifyContent='center'
						sx={{
							position: 'fixed',
							top: 400,
							left: mediaMd ? 300 : 0,
							right: 0,
							margin: '0 auto',
						}}>
						<CircularProgress sx={{ color: mainGreen }} />
					</Box>
				) : (
					<Box
						sx={{
							maxWidth: 1000,
							padding: '10px',
							margin: '0 auto 20px auto',
							background: '#fff',
							boxShadow: 'rgba(9, 30, 66, 0.25) 0px 4px 8px -2px, rgba(9, 30, 66, 0.08) 0px 0px 0px 1px',
							zIndex: 99,
						}}>
						<>
							<Box display='flex' justifyContent='center' sx={{ mb: 1 }}>
								<Chip sx={{ color: '#fff', background: mainGreen }} label='I. THỐNG KÊ THEO THỜI GIAN' />
							</Box>
							<PickDate setTime={setTime} time={timePick} />
							<Box sx={{ margin: '0 auto', height: '255px', width: '100%' }}>
								<Pie
									data={{
										labels: statistics?.map((store) => store.storeName),
										datasets: [
											{
												label: '% Sản lượng',
												data: statistics?.map((store) => store.percentage.totalQuantityPercentage),
												backgroundColor: [
													'#FF6361',
													'#FFA600',
													mainBlue,
													mainGreen,
													mainRed,
													'#BC5090',
													'#432E33',
													'#919EAB',
													'#39CCCC',
													'#5250bc',
													'#B76E00',
													'#1939B7',
													'#F012BE',
													'#E99787',
													'#0F1F38',
													'#A0B084',
													'#5F968E',
													'#D1B280',
													'#594D46',
													'#31A2AC',
													'#3A5199',
													'#E5E2CA',
													'#5A5F37',
													'#AA4B41',
													'#301B28',
													'#7F152E',
													'#BBCF4A',
													'#4C3F54',
													'#FDD475',
													'#C9A66B',
													'#FA6E59',
													'#F7EFE2',
													'#4D648D',
													'#1E656D',
													'#E4EA8C',
												],
											},
										],
									}}
									options={{
										animation: { duration: 500 },
										radius: 80,
										maintainAspectRatio: false,
									}}
								/>
							</Box>
							<Box display='flex' justifyContent='center' sx={{ mt: 1, mb: 2 }}>
								<Typography
									sx={{
										fontSize: '14px',
										background: mainGreen,
										color: '#fff',
										padding: '0 10px',
										borderRadius: '5px',
									}}>
									% Sản lượng
								</Typography>
							</Box>
							<Divider />
							<Box sx={{ margin: '10px auto', height: '255px', width: '100%' }}>
								<Pie
									data={{
										labels: statistics?.map((store) => store.storeName),
										datasets: [
											{
												label: '% Doanh thu',
												data: statistics?.map((store) => store.percentage.totalRevenuePercentage),
												backgroundColor: [
													'#FF6361',
													'#FFA600',
													mainBlue,
													mainGreen,
													mainRed,
													'#BC5090',
													'#432E33',
													'#919EAB',
													'#39CCCC',
													'#5250bc',
													'#B76E00',
													'#1939B7',
													'#F012BE',
													'#E99787',
													'#0F1F38',
													'#A0B084',
													'#5F968E',
													'#D1B280',
													'#594D46',
													'#31A2AC',
													'#3A5199',
													'#E5E2CA',
													'#5A5F37',
													'#AA4B41',
													'#301B28',
													'#7F152E',
													'#BBCF4A',
													'#4C3F54',
													'#FDD475',
													'#C9A66B',
													'#FA6E59',
													'#F7EFE2',
													'#4D648D',
													'#1E656D',
													'#E4EA8C',
												],
											},
										],
									}}
									options={{
										animation: { duration: 500 },
										radius: 80,
										maintainAspectRatio: false,
									}}
								/>
							</Box>
							<Box display='flex' justifyContent='center' sx={{ mt: 1, mb: 2 }}>
								<Typography
									sx={{
										fontSize: '14px',
										background: mainGreen,
										color: '#fff',
										padding: '0 10px',
										borderRadius: '5px',
									}}>
									% Doanh thu
								</Typography>
							</Box>
							<Divider />
							<Bar
								style={{ marginBottom: '10px', marginTop: '10px' }}
								data={{
									labels: statistics?.map((store) => store.storeName),
									datasets: [
										{
											label: 'Sản lượng (kg)',
											data: statistics?.map((store) => store.statistics.totalQuantity),
											backgroundColor: [mainRed],
											borderWidth: 1,
											borderColor: '#000',
										},
									],
								}}
								options={{
									animation: { duration: 500 },
								}}
							/>
							<Divider />

							<Bar
								style={{ marginBottom: '10px', marginTop: '10px' }}
								data={{
									labels: statistics?.map((store) => store.storeName),
									datasets: [
										{
											label: 'Doanh thu (ngàn đồng)',
											data: statistics?.map((store) => store.statistics.totalRevenue / 1000),
											backgroundColor: [mainGreen],
											borderWidth: 1,
											borderColor: '#000',
										},
									],
								}}
								options={{
									animation: { duration: 500 },
								}}
							/>
							<Divider />
							<Box display='flex' justifyContent='center' sx={{ mb: 1, mt: 1 }}>
								<Chip
									sx={{
										color: '#fff',
										background: mainGreen,
									}}
									label='II. SO SÁNH GIỮA 2 THỜI ĐIỂM'
								/>
							</Box>
							<PickDateWithoutShift
								timeLabel={1}
								labelBackground={mainGreen}
								labelColor={'#fff'}
								setTime={setFirstTime}
								time={firstTimePick}
							/>
							<PickDateWithoutShift
								timeLabel={2}
								labelBackground={'#FFAB00'}
								labelColor={'#000'}
								setTime={setSecondTime}
								time={secondTimePick}
							/>
							<Box display='flex' justifyContent='center'>
								<Button
									sx={{
										textTransform: 'none',
										padding: '0 10px',
										mt: 1,
										background: mainGreen,
										'&:hover': { background: mainGreen },
									}}
									variant='contained'
									size='small'
									onClick={() => {
										postCompareStatistics()
									}}>
									Xác nhận
								</Button>
							</Box>
							<Bar
								style={{ marginBottom: '10px', marginTop: '10px' }}
								data={{
									labels: statistics?.map((store) => store.storeName),
									datasets: [
										{
											label: `${firstTimePick.date}${firstTimePick.month}${firstTimePick.year}`,
											data: statisticsCompare?.map((store) => store.firstStatistics.firstTotalQuantity),
											backgroundColor: [mainGreen],
											borderWidth: 1,
											borderColor: '#000',
										},
										{
											label: `${secondTimePick.date}${secondTimePick.month}${secondTimePick.year}`,
											data: statisticsCompare?.map((store) => store.secondStatistics.secondTotalQuantity),
											backgroundColor: ['#FFAB00'],
											borderWidth: 1,
											borderColor: '#000',
										},
									],
								}}
								options={{
									animation: { duration: 500 },
								}}
							/>
							<Box display='flex' justifyContent='center' sx={{ mt: 1, mb: 2 }}>
								<Typography
									sx={{
										fontSize: '14px',
										background: mainGreen,
										color: '#fff',
										padding: '0 10px',
										borderRadius: '5px',
									}}>
									Sản lượng (kg)
								</Typography>
							</Box>
							<Divider />
							<Bar
								style={{ marginBottom: '10px', marginTop: '10px' }}
								data={{
									labels: statistics?.map((store) => store.storeName),
									datasets: [
										{
											label: `${firstTimePick.date}${firstTimePick.month}${firstTimePick.year}`,
											data: statisticsCompare?.map((store) => store.firstStatistics.firstTotalRevenue / 1000),
											backgroundColor: [mainGreen],
											borderWidth: 1,
											borderColor: '#000',
										},
										{
											label: `${secondTimePick.date}${secondTimePick.month}${secondTimePick.year}`,
											data: statisticsCompare?.map((store) => store.secondStatistics.secondTotalRevenue / 1000),
											backgroundColor: ['#FFAB00'],
											borderWidth: 1,
											borderColor: '#000',
										},
									],
								}}
								options={{
									animation: { duration: 500 },
								}}
							/>
							<Box display='flex' justifyContent='center' sx={{ mt: 1, mb: 2 }}>
								<Typography
									sx={{
										fontSize: '14px',
										background: mainGreen,
										color: '#fff',
										padding: '0 10px',
										borderRadius: '5px',
									}}>
									Doanh thu (ngàn đồng)
								</Typography>
							</Box>
							<Divider />
						</>
					</Box>
				)}
			</Box>
		</UserLayout>
	)
}
